import type { ExpenditureBudgetPurposeModel } from '@/features/master-table/models';
import { convertBudgetTypeNameToOrderingKey } from '@/features/project/_utils/budget-and-execution';
import type { ExpenditureCartsModel } from '@/features/project/projects/_models/expenditure-cart';
import type { ExpenditureItemsModel } from '@/features/project/projects/_models/expenditure-item';
import type { SubProjectsModel } from '@/features/project/projects/_models/sub-project';
import { BigCalculator, NEST_ITEM_CONNECTOR, type Recursive } from '@/utils';
import dayjs from 'dayjs';
import type { z } from 'zod';

const pairsToCascaderChain = (
  pairs: { id: string; name: string }[],
): Recursive<{ label: string; value: string }>[] => {
  if (!pairs || pairs.length === 0) {
    return [];
  }

  const buildNestedStructure = (
    items: { id: string; name: string }[],
    index: number,
  ): Recursive<{ label: string; value: string }>[] => {
    if (index >= items.length) {
      return [];
    }

    const currentItem = items[index];
    const option: Recursive<{ label: string; value: string }> = {
      label: currentItem?.name ?? '',
      value: currentItem?.id ?? '',
      children: [],
    };

    if (index < items.length - 1) {
      option.children = buildNestedStructure(items, index + 1);
    }

    return [option];
  };

  return buildNestedStructure(pairs, 0);
};

export const getExpenditureBudgetPurposeConcatData = ({
  data,
  withoutAccountingName,
}: {
  data: z.infer<typeof ExpenditureBudgetPurposeModel>;
  withoutAccountingName?: boolean;
}) => {
  const names = [
    withoutAccountingName ? undefined : data.accountingType.name,
    data.jurisdiction.name,
    data.accountingType.type === 'general'
      ? data.organization?.name
      : data.accountTitle?.name,
    data.expenditurePurpose.upper?.name,
    data.expenditurePurpose.name,
  ].filter(Boolean) as string[];

  const ids = [
    withoutAccountingName ? undefined : data.accountingId,
    data.jurisdictionId,
    data.accountingType.type === 'general'
      ? data.organization?.id
      : data.accountTitle?.id,
    data.expenditurePurpose.upper?.id,
    data.id,
  ].filter(Boolean) as string[];

  return {
    name: names.join(NEST_ITEM_CONNECTOR),
    ids,
    options: pairsToCascaderChain(
      ids.map((id, idx) => {
        return {
          id,
          name: names[idx] ?? '',
        };
      }),
    ),
  };
};

export const getExpenditureBudgetPurposeTableName = (
  data: z.infer<typeof ExpenditureBudgetPurposeModel> | null,
  expenditureBudgetPurposeDescription: string | null | undefined,
) => {
  return data
    ? getExpenditureBudgetPurposeConcatData({ data }).name
    : expenditureBudgetPurposeDescription;
};

export const sortExpenditureItemsData = (
  data: z.infer<typeof ExpenditureItemsModel>,
) => {
  return data.sort((a, b) => {
    const budgetTypeOrder =
      convertBudgetTypeNameToOrderingKey(a.budgetType.name) -
      convertBudgetTypeNameToOrderingKey(b.budgetType.name);
    if (budgetTypeOrder !== 0) {
      return budgetTypeOrder;
    }

    const descendingOrderUpdatedAt = dayjs(b.updatedAt).diff(
      dayjs(a.updatedAt),
    );

    const aHasDescription = !!a.expenditureBudgetPurposeDescription;
    const bHasDescription = !!b.expenditureBudgetPurposeDescription;
    if (aHasDescription && !bHasDescription) {
      return 1;
    }
    if (!aHasDescription && bHasDescription) {
      return -1;
    }

    const aFinalizedAmount = a.finalizedAmount ?? 0;
    const bFinalizedAmount = b.finalizedAmount ?? 0;
    if (
      !(aHasDescription && bHasDescription) &&
      bFinalizedAmount !== aFinalizedAmount
    ) {
      return BigCalculator.subtract(bFinalizedAmount, aFinalizedAmount);
    }
    return descendingOrderUpdatedAt;
  });
};

// To make variable's type optional
export const getGarageBySubProjects = (
  subProjects: z.infer<typeof SubProjectsModel>,
) => subProjects.at(0);

type ExpenditureCartsType = z.infer<typeof ExpenditureCartsModel>;

export const splitExpenditureCartsByAccountingType = (
  data: ExpenditureCartsType,
) => {
  const { general, special } = data.reduce<{
    general: ExpenditureCartsType;
    special: ExpenditureCartsType;
  }>(
    (acc, cart) => {
      switch (cart.accountingType.type) {
        case 'general':
          acc.general.push(cart);
          break;
        case 'special':
          acc.special.push(cart);
          break;
      }
      return acc;
    },
    { general: [], special: [] },
  );

  return [general, special].filter((v) => v.length > 0);
};
